import { useState, useEffect } from 'react'
import { saveAIConfig, getAIConfig, AIProvider, GeminiModel, ChatGPTModel } from '../services/aiService'
import './SettingsAI.css'

const GEMINI_MODELS: GeminiModel[] = ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-2.0-flash', 'gemini-2.0-flash-001']
const CHATGPT_MODELS: ChatGPTModel[] = ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-4', 'gpt-3.5-turbo']

export function SettingsAI() {
  const [provider, setProvider] = useState<AIProvider>('gemini')
  const [geminiModel, setGeminiModel] = useState<GeminiModel>('gemini-2.5-flash')
  const [chatgptModel, setChatgptModel] = useState<ChatGPTModel>('gpt-4o-mini')
  const [chatgptApiKey, setChatgptApiKey] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    // Load current configuration
    const config = getAIConfig()
    setProvider(config.provider)
    setGeminiModel(config.geminiModel || 'gemini-2.5-flash')
    setChatgptModel(config.chatgptModel || 'gpt-4o-mini')
    // Don't load API key for security - user needs to re-enter it
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setSaveMessage(null)

    try {
      // Validate ChatGPT API key if ChatGPT is selected
      if (provider === 'chatgpt' && !chatgptApiKey.trim()) {
        setSaveMessage({ type: 'error', text: 'ChatGPT API key is required when using ChatGPT provider.' })
        setSaving(false)
        return
      }

      saveAIConfig({
        provider,
        geminiModel,
        chatgptModel,
        chatgptApiKey: provider === 'chatgpt' ? chatgptApiKey.trim() : undefined,
      })

      setSaveMessage({ type: 'success', text: 'AI configuration saved successfully!' })
      setTimeout(() => setSaveMessage(null), 3000)
    } catch (error: any) {
      setSaveMessage({ type: 'error', text: error.message || 'Failed to save configuration' })
    } finally {
      setSaving(false)
    }
  }

  const getModelDisplayName = (model: string): string => {
    const modelNames: Record<string, string> = {
      'gemini-2.5-flash': 'Gemini 2.5 Flash',
      'gemini-2.5-pro': 'Gemini 2.5 Pro',
      'gemini-2.0-flash': 'Gemini 2.0 Flash',
      'gemini-2.0-flash-001': 'Gemini 2.0 Flash 001',
      'gpt-4o': 'GPT-4o',
      'gpt-4o-mini': 'GPT-4o Mini',
      'gpt-4-turbo': 'GPT-4 Turbo',
      'gpt-4': 'GPT-4',
      'gpt-3.5-turbo': 'GPT-3.5 Turbo',
    }
    return modelNames[model] || model
  }

  return (
    <div className="settings-ai">
      <div className="settings-ai-header">
        <h2>AI Configuration</h2>
        <p className="settings-ai-subtitle">Configure AI provider and model for consultation suggestions</p>
      </div>

      <div className="settings-ai-content">
        <div className="settings-ai-section">
          <h3>AI Provider</h3>
          <div className="form-group">
            <label htmlFor="provider">Provider</label>
            <select
              id="provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value as AIProvider)}
              className="form-control"
            >
              <option value="gemini">Google Gemini</option>
              <option value="chatgpt">OpenAI ChatGPT</option>
            </select>
            <p className="form-hint">Select the AI provider to use for generating consultation suggestions.</p>
          </div>
        </div>

        {provider === 'gemini' && (
          <div className="settings-ai-section">
            <h3>Gemini Model</h3>
            <div className="form-group">
              <label htmlFor="gemini-model">Model</label>
              <select
                id="gemini-model"
                value={geminiModel}
                onChange={(e) => setGeminiModel(e.target.value as GeminiModel)}
                className="form-control"
              >
                {GEMINI_MODELS.map((model) => (
                  <option key={model} value={model}>
                    {getModelDisplayName(model)}
                  </option>
                ))}
              </select>
              <p className="form-hint">Select the Gemini model to use. Flash models are faster, Pro models are more capable.</p>
            </div>
          </div>
        )}

        {provider === 'chatgpt' && (
          <>
            <div className="settings-ai-section">
              <h3>ChatGPT Model</h3>
              <div className="form-group">
                <label htmlFor="chatgpt-model">Model</label>
                <select
                  id="chatgpt-model"
                  value={chatgptModel}
                  onChange={(e) => setChatgptModel(e.target.value as ChatGPTModel)}
                  className="form-control"
                >
                  {CHATGPT_MODELS.map((model) => (
                    <option key={model} value={model}>
                      {getModelDisplayName(model)}
                    </option>
                  ))}
                </select>
                <p className="form-hint">Select the ChatGPT model to use. GPT-4o is the latest, GPT-4o Mini is faster and cheaper.</p>
              </div>
            </div>

            <div className="settings-ai-section">
              <h3>ChatGPT API Key</h3>
              <div className="form-group">
                <label htmlFor="chatgpt-api-key">API Key</label>
                <div className="api-key-input-wrapper">
                  <input
                    id="chatgpt-api-key"
                    type={showApiKey ? 'text' : 'password'}
                    value={chatgptApiKey}
                    onChange={(e) => setChatgptApiKey(e.target.value)}
                    placeholder="sk-..."
                    className="form-control"
                  />
                  <button
                    type="button"
                    className="toggle-api-key-btn"
                    onClick={() => setShowApiKey(!showApiKey)}
                    title={showApiKey ? 'Hide API key' : 'Show API key'}
                  >
                    {showApiKey ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                <p className="form-hint">
                  Enter your OpenAI API key. Get one at{' '}
                  <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">
                    platform.openai.com/api-keys
                  </a>
                  . Your API key is stored locally and never sent to our servers.
                </p>
              </div>
            </div>
          </>
        )}

        {saveMessage && (
          <div className={`save-message ${saveMessage.type}`}>
            {saveMessage.text}
          </div>
        )}

        <div className="settings-ai-actions">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  )
}






























